// useChangeLog.js
// ---------------------------------------------------------------------------
// Durable, shared change log for the Survey Builder.
//
// Captures each semantic edit to `sdataGroup` as a log entry (debounced, like
// undo), attributes it to the current user, broadcasts it live to collaborators,
// and supports selective revert with the "later change wins" merge rule.
//
// Capture is skipped whenever `suppressRef.current` is true — the caller sets
// this flag right before applying a change that must NOT be logged locally:
// initial load, remote socket applies, undo/redo restores, and revert applies.
// ---------------------------------------------------------------------------
import { useRef, useEffect, useState, useCallback } from "react";
import apiService from "../../services/apiService";
import {
  diffDataGroups,
  describeOps,
  applyRevert,
  setAtPath,
} from "../../utils/changeLogUtils";

const clone = (v) => (v == null ? v : JSON.parse(JSON.stringify(v)));

const newUuid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `c_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

// Newest first.
const sortEntries = (list) =>
  [...list].sort((a, b) =>
    (b.createdAt || "").localeCompare(a.createdAt || "")
  );

// Apply a lightweight [{ path, value }] patch to a data group (immutably).
// A missing `value` (undefined, dropped by JSON) means "delete this field".
const applyPatch = (group, patch = []) => {
  let next = { ...group };
  patch.forEach((item) => {
    const hasVal = Object.prototype.hasOwnProperty.call(item, "value");
    next = setAtPath(next, item.path, hasVal ? item.value : undefined);
  });
  return next;
};

export function useChangeLog({
  sdataGroup,
  setSdataGroup,
  projectId,
  username,
  userEmail,
  suppressRef,
  emitChangelogAdd,
  emitChangelogRevert,
  onApplyExternal,
  debounceMs = 500,
  armDelayMs = 1500,
}) {
  const [entries, setEntries] = useState([]); // newest first
  const prevSnapshot = useRef(null); // last committed snapshot for diffing
  const debounceTimer = useRef(null);
  const armTimer = useRef(null);
  const armed = useRef(false); // false during initial load/hydration
  const unpersisted = useRef(new Set());

  // Fetch persisted entries for the current project.
  const reload = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await fetch(
        `${apiService.baseURL}/survey-changelog/${projectId}`,
        { credentials: "include" }
      );
      const data = await res.json();
      if (data?.success && Array.isArray(data.entries)) {
        setEntries(sortEntries(data.entries));
      }
    } catch (err) {
      console.error("Failed to load change log:", err);
    }
  }, [projectId]);

  // Reset baseline + reload when the project changes. Capture stays DISARMED
  // until the data settles (see watcher) so the initial survey load
  // ({} → full survey) and imports are absorbed into the baseline, not logged.
  useEffect(() => {
    prevSnapshot.current = null;
    unpersisted.current = new Set();
    armed.current = false;
    clearTimeout(armTimer.current);
    setEntries([]);
    reload();
  }, [projectId, reload]);

  // Debounced capture watcher.
  useEffect(() => {
    const cur = sdataGroup;
    if (cur == null) return undefined;

    // Arm capture once the (non-empty) data has been quiet for armDelayMs, so
    // the initial load/imports settle into the baseline before we log edits.
    const scheduleArm = () => {
      if (armed.current) return;
      if (!cur || Object.keys(cur).length === 0) return;
      clearTimeout(armTimer.current);
      armTimer.current = setTimeout(() => {
        armed.current = true;
      }, armDelayMs);
    };

    // First observed state = baseline (no entry).
    if (prevSnapshot.current === null) {
      prevSnapshot.current = clone(cur);
      scheduleArm();
      return undefined;
    }

    // Not yet armed (initial load/hydration window), or a programmatic apply
    // (remote / undo/redo / revert) — advance the baseline, log nothing.
    if (!armed.current || suppressRef?.current) {
      suppressRef.current = false;
      prevSnapshot.current = clone(cur);
      clearTimeout(debounceTimer.current);
      scheduleArm();
      return undefined;
    }

    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      const prev = prevSnapshot.current;
      const ops = diffDataGroups(prev, cur);
      if (!ops.length) return;

      const entry = {
        entryUuid: newUuid(),
        projectId,
        username,
        userEmail,
        createdAt: new Date().toISOString(),
        description: describeOps(ops, prev, cur),
        ops,
        reverted: false,
        revertOf: null,
      };
      prevSnapshot.current = clone(cur);
      unpersisted.current.add(entry.entryUuid);
      setEntries((prevList) => sortEntries([entry, ...prevList]));
      if (emitChangelogAdd) emitChangelogAdd(entry);
    }, debounceMs);

    return () => clearTimeout(debounceTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdataGroup]);

  // Persist all known entries (idempotent upsert). Call on Save.
  const persistEntries = useCallback(async () => {
    if (!projectId || entries.length === 0) return;
    try {
      await fetch(`${apiService.baseURL}/survey-changelog/${projectId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ entries }),
      });
      unpersisted.current.clear();
    } catch (err) {
      console.error("Failed to persist change log:", err);
    }
  }, [projectId, entries]);

  // Revert a specific entry, keeping later edits ("later wins").
  const revertEntry = useCallback(
    (entryUuid) => {
      const idx = entries.findIndex((e) => e.entryUuid === entryUuid);
      if (idx === -1) return;
      const target = entries[idx];
      if (target.reverted) return;

      // entries are newest-first → later-in-time entries are those before idx.
      const laterEntries = entries.slice(0, idx);
      const newState = applyRevert(sdataGroup, target, laterEntries);

      // Field-level diff of what the revert actually changed (for entry + patch).
      const changeOps = diffDataGroups(sdataGroup, newState);
      const patch = changeOps.map((op) =>
        op.after === undefined
          ? { path: op.path }
          : { path: op.path, value: op.after }
      );

      const revertEntryObj = {
        entryUuid: newUuid(),
        projectId,
        username,
        userEmail,
        createdAt: new Date().toISOString(),
        description: `Reverted "${target.description}"`,
        ops: changeOps,
        reverted: false,
        revertOf: entryUuid,
      };

      // Apply locally without re-capturing it as a fresh diff.
      if (suppressRef) suppressRef.current = true;
      setSdataGroup(newState);
      if (onApplyExternal) onApplyExternal(newState);

      setEntries((prev) =>
        sortEntries([
          revertEntryObj,
          ...prev.map((e) =>
            e.entryUuid === entryUuid ? { ...e, reverted: true } : e
          ),
        ])
      );
      unpersisted.current.add(revertEntryObj.entryUuid);
      unpersisted.current.add(entryUuid);

      if (emitChangelogRevert) {
        emitChangelogRevert({
          revertEntry: revertEntryObj,
          revertedUuid: entryUuid,
          patch,
        });
      }
    },
    [
      entries,
      sdataGroup,
      setSdataGroup,
      projectId,
      username,
      userEmail,
      suppressRef,
      onApplyExternal,
      emitChangelogRevert,
    ]
  );

  // ── Remote (collaborator) events, wired from the websocket layer ──────────
  const addRemoteEntry = useCallback((entry) => {
    if (!entry?.entryUuid) return;
    setEntries((prev) =>
      prev.some((e) => e.entryUuid === entry.entryUuid)
        ? prev
        : sortEntries([entry, ...prev])
    );
  }, []);

  const applyRemoteRevert = useCallback(
    ({ revertEntry: revEntry, revertedUuid, patch }) => {
      if (suppressRef) suppressRef.current = true;
      setSdataGroup((prev) => {
        const next = applyPatch(prev || {}, patch || []);
        if (onApplyExternal) onApplyExternal(next);
        return next;
      });
      setEntries((prev) => {
        const marked = prev.map((e) =>
          e.entryUuid === revertedUuid ? { ...e, reverted: true } : e
        );
        if (revEntry && !marked.some((e) => e.entryUuid === revEntry.entryUuid)) {
          return sortEntries([revEntry, ...marked]);
        }
        return marked;
      });
    },
    [setSdataGroup, suppressRef, onApplyExternal]
  );

  return {
    entries,
    revertEntry,
    persistEntries,
    reload,
    addRemoteEntry,
    applyRemoteRevert,
  };
}

export default useChangeLog;
